import { DeleteOutline } from '@mui/icons-material';
import { Box, styled, TextareaAutosize, useTheme } from '@mui/material';
import { Stack } from '@mui/system';
import React, { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import EconsultLabResultLines from '../econsultLabResultLines/EconsultLabResultLines';
import EconsultPathologyReports from '../econsultPathologyReports/EconsultPathologyReports';
import EconsultReportBlocks from '../econsultReportBlocks/EconsultReportBlocks';

interface EconsultClinicalReportCategorySectionProps {
  noteContent: string;
  onContentChanged: (value: string) => void;
  onDelete: () => void;
  readOnly?: boolean;
  isLabResult?: boolean;
  isPathologyReport?: boolean;
  isDiagnosticReport?: boolean;
  isEncounter?: boolean;
}

const StyledTextAreaAutoResize = styled(TextareaAutosize)(({ theme }) => ({
  '&::placeholder': {
    fontSize: '0.875rem',
    color: theme.palette.base.grey5
  },
  color: 'rgba(0, 0, 0, 0.87)',
  '&[aria-hidden="true"]': {
    height: '0 !important',
    minHeight: '0 !important',
    maxHeight: '0 !important',
    overflow: 'hidden !important',
    visibility: 'hidden',
    pointerEvents: 'none'
  }
}));

const EconsultClinicalReportCategorySection: FC<EconsultClinicalReportCategorySectionProps> = ({
  noteContent,
  onContentChanged,
  onDelete,
  readOnly = false,
  isLabResult = false,
  isPathologyReport = false,
  isDiagnosticReport = false,
  isEncounter = false
}) => {
  const theme = useTheme();
  const [localNoteContent, setLocalNoteContent] = useState<string>(noteContent);
  const isFocusedRef = useRef<boolean>(false);
  const hasUserEditedWhileFocusedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!isFocusedRef.current || !hasUserEditedWhileFocusedRef.current) {
      setLocalNoteContent(noteContent);
    }
  }, [noteContent]);

  const requestTextAreaStyle = {
    width: '100%',
    resize: 'vertical',
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.body2.fontSize,
    height: 180,
    overflow: 'auto',
    borderColor: theme.palette.base.grey5,
    paddingLeft: 5
  };

  const handleNoteContentChanged = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = event.target;
    hasUserEditedWhileFocusedRef.current = true;
    setLocalNoteContent(value);
    onContentChanged(value);
  };

  const renderNoteContent = () => {
    if (isPathologyReport) return <EconsultPathologyReports content={noteContent} />;
    if (isLabResult) return <EconsultLabResultLines content={noteContent} />;
    if (isDiagnosticReport) {
      return <EconsultReportBlocks content={noteContent} headingLineCount={2} lineSpacing={1} />;
    }
    if (isEncounter) {
      return (
        <EconsultReportBlocks
          content={noteContent}
          boldNonNarrativeLines
          blockSpacing={5}
          sectionSpacing={3}
        />
      );
    }
    return <EconsultReportBlocks content={noteContent} />;
  };

  return (
    <Stack direction="row">
      {readOnly ? (
        <Box
          sx={{
            width: '100%',
            fontSize: theme.typography.body2.fontSize,
            minHeight: `calc(${theme.typography.body2.lineHeight} * 3em + 10px)`,
            height: 180,
            resize: 'vertical',
            overflow: 'auto',
            border: 1,
            borderColor: 'base.grey5',
            borderRadius: '3px',
            px: '5px',
            py: '4px'
          }}>
          {renderNoteContent()}
        </Box>
      ) : (
        <StyledTextAreaAutoResize
          onChange={handleNoteContentChanged}
          onFocus={() => {
            isFocusedRef.current = true;
            hasUserEditedWhileFocusedRef.current = false;
          }}
          onBlur={() => {
            isFocusedRef.current = false;
            hasUserEditedWhileFocusedRef.current = false;
            setLocalNoteContent(noteContent);
          }}
          minRows={3}
          maxRows={10}
          value={localNoteContent}
          style={requestTextAreaStyle}
          name="requestNte"
        />
      )}
      <DeleteOutline sx={{ color: 'primary.main' }} onClick={onDelete} />
    </Stack>
  );
};

export default EconsultClinicalReportCategorySection;
